import { BaseService } from '../base/BaseService';
import { ServiceError } from '../base/ServiceError';
import type { QueryOptions, ServiceResponse } from '../base/ServiceTypes';

import type { Database } from '@/integrations/supabase/types';

type CompetitorRow = Database['public']['Tables']['competitors']['Row'];
export type CompetitorRelationshipKind =
  Database['public']['Enums']['competitor_relationship_kind'];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface RelationshipKindFilter {
  /** Restrict to these relationship_kind values (e.g. exclude client_account/inspiration rows from a strict competitor list). Omit for all kinds. */
  relationshipKinds?: CompetitorRelationshipKind[];
}

export type CompetitorListOptions =
  | (QueryOptions &
      RelationshipKindFilter & {
        brandKitId: string;
        includeUnassigned?: boolean;
        remediation?: never;
      })
  | (QueryOptions & RelationshipKindFilter & { remediation: 'unassigned'; brandKitId?: never });

export interface CompetitorDeleteOptions {
  brandKitId: string;
}

export class CompetitorService extends BaseService {
  private static instance: CompetitorService;

  private constructor() {
    super({
      defaultTTL: 10 * 60 * 1000, // 10 minutes for competitors
      enableCache: true,
      enableRetry: true,
    });
  }

  public static getInstance(): CompetitorService {
    if (!CompetitorService.instance) {
      CompetitorService.instance = new CompetitorService();
    }
    return CompetitorService.instance;
  }

  private validatedBrandKitId(brandKitId: string): string {
    const normalizedKitId = brandKitId.trim();
    if (!UUID_RE.test(normalizedKitId)) {
      throw ServiceError.validationError('A valid brand kit is required to manage competitors');
    }
    return normalizedKitId;
  }

  /**
   * Get all competitors for the current user
   */
  async getCompetitors(options: CompetitorListOptions): Promise<ServiceResponse<CompetitorRow[]>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();
      const { remediation, brandKitId, relationshipKinds, ...queryOptions } = options;
      const includeUnassigned =
        'includeUnassigned' in options && options.includeUnassigned === true;
      const normalizedKitId = brandKitId ? this.validatedBrandKitId(brandKitId) : null;
      if (!normalizedKitId && remediation !== 'unassigned') {
        throw ServiceError.validationError(
          'Select a brand kit before viewing competitors, or explicitly request unassigned remediation',
        );
      }
      const kindsKey = relationshipKinds?.length
        ? `-${[...relationshipKinds].sort().join(',')}`
        : '';
      const cacheKey = normalizedKitId
        ? `competitors-${ownerId}-${normalizedKitId}${includeUnassigned ? '-including-unassigned' : ''}${kindsKey}`
        : `competitors-${ownerId}-unassigned${kindsKey}`;

      return this.executeWithCache(
        cacheKey,
        async () => {
          let scopedQuery = this.client.from('competitors').select('*').eq('user_id', ownerId);
          if (normalizedKitId) {
            // Generated Supabase types are refreshed in the migration's
            // follow-up; keep this bounded cast at the schema seam.
            scopedQuery = includeUnassigned
              ? scopedQuery.or(`brand_kit_id.eq.${normalizedKitId},brand_kit_id.is.null`)
              : scopedQuery.eq('brand_kit_id' as never, normalizedKitId);
          } else {
            scopedQuery = scopedQuery.is('brand_kit_id' as never, null);
          }
          if (relationshipKinds?.length) {
            scopedQuery = scopedQuery.in('relationship_kind', relationshipKinds);
          }
          const query = this.buildQuery(scopedQuery, {
            orderBy: 'created_at',
            orderDirection: 'desc',
            ...queryOptions,
          });

          const { data, error } = await query;

          if (error) throw error;

          return data || [];
        },
        queryOptions,
      );
    });
  }

  /**
   * Get a single competitor by ID
   */
  async getCompetitor(
    id: string,
    { brandKitId }: CompetitorDeleteOptions,
  ): Promise<ServiceResponse<CompetitorRow | null>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();
      const normalizedKitId = this.validatedBrandKitId(brandKitId);
      const cacheKey = `competitor-${ownerId}-${normalizedKitId}-${id}`;

      return this.executeWithCache(cacheKey, async () => {
        const { data, error } = await this.client
          .from('competitors')
          .select('*')
          .eq('id', id)
          .eq('user_id', ownerId)
          .eq('brand_kit_id' as never, normalizedKitId)
          .single();

        if (error) {
          if (error.code === 'PGRST116') {
            throw ServiceError.notFound('Competitor');
          }
          throw error;
        }

        return data;
      });
    });
  }

  /**
   * Delete a competitor
   */
  async deleteCompetitor(
    id: string,
    { brandKitId }: CompetitorDeleteOptions,
  ): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();
      const normalizedKitId = this.validatedBrandKitId(brandKitId);

      const { error } = await this.client
        .from('competitors')
        .delete()
        .eq('id', id)
        .eq('user_id', ownerId)
        // Generated Supabase types are refreshed in the migration's
        // follow-up; keep this bounded cast at the schema seam.
        .eq('brand_kit_id' as never, normalizedKitId);

      if (error) {
        if (error.code === 'PGRST116') {
          throw ServiceError.notFound('Competitor');
        }
        throw error;
      }

      this.clearCache(`competitor-${ownerId}-${normalizedKitId}-${id}`);
      this.clearKitListCaches(ownerId, normalizedKitId);
    }, 'Failed to delete competitor');
  }

  async invalidateKitCache(brandKitId: string): Promise<void> {
    const ownerId = await this.ensureAuthenticatedOwner();
    this.clearKitListCaches(ownerId, this.validatedBrandKitId(brandKitId));
  }

  private clearKitListCaches(ownerId: string, brandKitId: string): void {
    const prefix = `competitors-${ownerId}-${brandKitId}`;
    for (const key of [...this.getCacheStore().keys()]) {
      if (key === prefix || key.startsWith(`${prefix}-`)) this.clearCache(key);
    }
  }
}

// Export singleton instance
export const competitorService = CompetitorService.getInstance();
